/**
 * Shared contract for the driver-results QR payload.
 *
 * The hub embeds this self-contained snapshot in the results-page URL
 * (`/r/:eventKey/:sessionId?d=…`) so the page renders **offline** — when the
 * venue/hub had no internet at session end, the cloud has no record of the
 * session, so the page falls back to this payload. When the cloud DOES have the
 * session it wins (authoritative + live position); this is only the fallback.
 *
 * Encoding is `base64url(deflateRaw(JSON))` via `pako` — DEFLATE compression is
 * what makes a full race's laps + sectors fit a QR (a 40-lap payload shrinks
 * ~72%, keeping the URL well under the ~2.9 KB QR ceiling). pako runs in both
 * Node (hub, encode) and the browser (cloud-ui, decode). This module lives
 * behind the `@vms/types/results-share` subpath so pako stays out of consumers
 * that don't need it (the mobile bundle). Laps are still capped via
 * `capLapsForPayload` as a belt-and-braces bound; the online view always has
 * the full set. Branding carries only colours + asset URLs (no `customCss`).
 */
import { deflateRaw, inflateRaw } from "pako";
/**
 * Max laps embedded in the offline payload — keeps the QR dense-but-scannable.
 * The best lap is always kept; the rest are the most-recent laps. The online
 * view (cloud fetch) always shows the full set.
 */
export const RESULTS_QR_MAX_LAPS = 30;
// `@vms/types` compiles with neither DOM nor Node lib types, so reach btoa/atob
// (present in Node 18+ AND browsers) through a typed globalThis cast. This keeps
// the package env-agnostic and emits no ambient global declarations that could
// clash in consumers (hub=node, cloud-ui=dom). pako handles the UTF-8 ⇄ bytes
// step, so no TextEncoder/TextDecoder is needed.
const runtime = globalThis;
function bytesToBinary(bytes) {
    let s = "";
    for (let i = 0; i < bytes.length; i++)
        s += String.fromCharCode(bytes[i]);
    return s;
}
/** Encode a payload to a URL-safe string: base64url(deflateRaw(UTF-8 JSON)). */
export function encodeResultsPayload(payload) {
    // pako deflates a JS string as UTF-8; returns a Uint8Array.
    const compressed = deflateRaw(JSON.stringify(payload));
    return runtime
        .btoa(bytesToBinary(compressed))
        .replace(/\+/g, "-")
        .replace(/\//g, "_")
        .replace(/=+$/, "");
}
/** Decode a payload string, or null if it's missing / malformed / wrong version. */
export function decodeResultsPayload(encoded) {
    if (!encoded)
        return null;
    try {
        const b64 = encoded.replace(/-/g, "+").replace(/_/g, "/");
        const bin = runtime.atob(b64);
        const bytes = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++)
            bytes[i] = bin.charCodeAt(i);
        const json = inflateRaw(bytes, { to: "string" });
        const parsed = JSON.parse(json);
        if (!parsed || typeof parsed !== "object" || parsed.v !== 1)
            return null;
        return parsed;
    }
    catch {
        return null;
    }
}
/**
 * Trim a full lap list to the payload cap so the QR stays scannable: always keep
 * the (valid) best lap, plus the most-recent laps up to the cap, restored to lap
 * order. A no-op when already under the cap.
 */
export function capLapsForPayload(laps) {
    if (laps.length <= RESULTS_QR_MAX_LAPS)
        return laps;
    const bestLap = laps.reduce((best, l) => {
        if (!l.v)
            return best;
        if (!best || l.ms < best.ms)
            return l;
        return best;
    }, null);
    const kept = new Map();
    for (const l of [...laps].sort((a, b) => b.n - a.n).slice(0, RESULTS_QR_MAX_LAPS)) {
        kept.set(l.n, l);
    }
    if (bestLap)
        kept.set(bestLap.n, bestLap);
    return Array.from(kept.values()).sort((a, b) => a.n - b.n);
}
//# sourceMappingURL=results-share.js.map